/* ==========================================================================================
   EcoVessel 2026 · ev-header.js · behaviour of sections/ev-header.liquid (header v2 builder, 02-10-2026;
   spec docs/HEADER-V2.md). Deferred, idempotent, re-binds on shopify:section:load. Needs nothing but the markup; uses
   window.BO (ev-main.js) when it is there, for anything inside a copied panel.
     - the sticky bar: once the page scrolls past ~70px the row glides into the slim frosted bar (.is-compact on the
       header, html.ev-hdr-compact); scrolling DOWN tucks the navy announcement bar away (html.ev-hdr-tucked),
       scrolling UP brings it back; back near the top the full row returns. Only scrollY is read on a scroll frame
       (one rAF per frame); the look is CSS (transform / opacity only). Frozen while a panel, the search or the drawer
       is open, so nothing moves under the pointer.
     - --ev-header-h / --header-height on :root: the pinned bar's height (announcement + slim bar), for other sticky
       pieces (collection sidebar, cart summary, product gallery). Constant while scrolling: nothing else jumps.
     - the mega panels: hover with intent, click / tap / Enter / arrow-down, Escape, click outside, the dimmed page,
       photos asked for only once the pointer or keyboard reaches the header; --ev-hdr-bottom (where the bar ends) is
       written when a panel opens, so the panel's tiles take their size from the room that is left
     - the search overlay: the search icon opens it and focuses the big field; Escape, the close button or a click on
       the dimmed page closes it; focus stays inside while it is open and goes back to the icon after
     - the navigation fits itself: below a comfortable width the gaps tighten, then the words step down a little
     - the phone drawer: level 2 is copied from the computer panel on first use (the menu is in the page once),
       focus is kept inside, Escape closes
     - something goes into the cart (ev:cart:added, or the count goes up): the bag hops, the count pops, the logo glints
   Nothing moves with prefers-reduced-motion (the CSS switches every transition and animation off).
   ========================================================================================== */
(() => {
  'use strict';
  if (window.EVHeader) { window.EVHeader.init(document); return; }

  const W = window, D = document, H = D.documentElement;
  const mqDesk = W.matchMedia('(min-width: 1200px)');
  const mqReduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const ON = 72, OFF = 8;                 // compact once scrolled past 72px; the full row again above 8px
  const TUCK = 28, UNTUCK = 18;           // px of steady scrolling down / up before the navy bar tucks / comes back
  const OPEN_DELAY = 120, SWITCH_DELAY = 40, CLOSE_DELAY = 240;

  const ICON_CHEV_LEFT = '<svg class="ev-icon" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5.5L8.5 12l6.5 6.5"/></svg>';
  const ICON_PLUS = '<svg class="ev-icon" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>';

  const focusablesIn = (root) => [...root.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])')]
    .filter((el) => el.offsetParent !== null && !el.closest('[hidden]') && getComputedStyle(el).visibility !== 'hidden');
  const px = (el, name) => parseFloat(getComputedStyle(el).getPropertyValue(name)) || 0;

  const bind = (hdr) => {
    if (hdr.evBound) return;
    hdr.evBound = true;
    const bars = hdr.querySelector('[data-ev-bars]');
    const bg = hdr.querySelector('[data-ev-bg]');
    const nav = hdr.querySelector('[data-ev-nav]');
    const scrim = hdr.querySelector('[data-ev-scrim]');
    const items = [...hdr.querySelectorAll('.ev-nav__item[data-ev-item]')];
    const sticky = hdr.dataset.sticky !== 'false';

    /* ---------- the pinned bar's height (for sticky pieces elsewhere) and where the bar ends (for the panels) ---------- */
    let lastH = -1;
    const writeH = () => {
      const ann = px(H, '--ev-ann-h');
      const h = Math.round((sticky ? ann : 0) + px(hdr, '--ev-row-c'));
      if (h === lastH || h < 1) return;
      lastH = h;
      H.style.setProperty('--ev-header-h', h + 'px');
      H.style.setProperty('--header-height', h + 'px');
    };
    const measure = () => {
      const r = (bg || bars).getBoundingClientRect();
      hdr.style.setProperty('--ev-hdr-bottom', Math.max(0, Math.round(r.bottom)) + 'px');
    };

    /* ---------- photos: asked for once the header is reached ---------- */
    let warmed = false;
    const warm = () => {
      if (warmed) return;
      warmed = true;
      hdr.querySelectorAll('.ev-mega img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    };

    /* ---------- the sticky bar: compact (slim, frosted, small wordmark) and the tucked navy bar ---------- */
    let compact = false, tucked = false, frozen = 0, lastY = W.scrollY, run = 0, ticking = false, glintT = 0;
    /* the wordmark catches the light once (ev-logo--pulse: one glint) */
    const glint = () => {
      if (mqReduce.matches) return;
      const logo = hdr.querySelector('.ev-hdr__logo .ev-logo');
      if (!logo) return;
      logo.classList.remove('ev-logo--pulse');
      void logo.getBoundingClientRect();
      logo.classList.add('ev-logo--pulse');
      clearTimeout(glintT);
      glintT = setTimeout(() => logo.classList.remove('ev-logo--pulse'), 900);
    };
    const setCompact = (on) => {
      if (on === compact) return;
      compact = on;
      hdr.classList.toggle('is-compact', on);
      H.classList.toggle('ev-hdr-compact', on);
      /* as the bar glides between full and slim, the wordmark catches the light (not on a page opened half way down) */
      if (!H.classList.contains('ev-hdr-instant') && mqDesk.matches) setTimeout(glint, 160);
    };
    const setTucked = (on) => {
      if (on === tucked) return;
      tucked = on;
      hdr.classList.toggle('is-tucked', on);
      H.classList.toggle('ev-hdr-tucked', on);
    };
    const update = () => {
      ticking = false;
      const y = Math.max(0, W.scrollY);
      const dy = y - lastY;
      lastY = y;
      if (!sticky || frozen) return;
      if (!compact && y > ON) setCompact(true);
      else if (compact && y < OFF) { setCompact(false); setTucked(false); run = 0; return; }
      if (!compact) { run = 0; return; }
      /* steady scrolling down tucks the navy bar, steady scrolling up brings it back */
      if (dy > 0) run = run > 0 ? run + dy : dy;
      else if (dy < 0) run = run < 0 ? run + dy : dy;
      if (run > TUCK) setTucked(true);
      else if (run < -UNTUCK) setTucked(false);
    };
    const onScroll = () => { if (!ticking) { ticking = true; W.requestAnimationFrame(update); } };
    const freeze = (on) => {
      frozen = Math.max(0, frozen + (on ? 1 : -1));
      if (!frozen) { lastY = W.scrollY; run = 0; onScroll(); }
    };
    if (sticky) {
      /* a page opened half way down (back button, refresh) starts in the slim bar, without the glide */
      if (W.scrollY > ON) {
        H.classList.add('ev-hdr-instant');
        setCompact(true);
        setTucked(true);
        W.requestAnimationFrame(() => W.requestAnimationFrame(() => H.classList.remove('ev-hdr-instant')));
      }
      W.addEventListener('scroll', onScroll, { passive: true });
    }

    /* ---------- mega panels ---------- */
    let openItem = null, openT = 0, closeT = 0, measureT = 0;
    const panelOf = (it) => it.querySelector('[data-ev-panel]');
    const setExpanded = (it, on) => it.querySelectorAll('[data-ev-toggle]').forEach((b) => b.setAttribute('aria-expanded', on ? 'true' : 'false'));
    const show = (it, instant) => {
      const p = panelOf(it);
      if (!p) return;
      measure();
      clearTimeout(measureT);
      measureT = setTimeout(measure, 480);   /* again once a glide of the bar (if one was running) has settled */
      p.hidden = false;
      if (instant || mqReduce.matches) { p.classList.add('is-open'); return; }
      void p.offsetWidth;
      p.classList.add('is-open');
    };
    const hide = (it, instant) => {
      const p = panelOf(it);
      if (!p) return;
      p.classList.remove('is-open');
      const done = () => { if (!p.classList.contains('is-open')) p.hidden = true; };
      if (instant || mqReduce.matches) done(); else setTimeout(done, 300);
    };
    const open = (it) => {
      clearTimeout(openT); clearTimeout(closeT);
      if (openItem === it) return;
      const switching = !!openItem;
      hdr.classList.toggle('is-switching', switching);
      if (openItem) { openItem.classList.remove('is-open'); setExpanded(openItem, false); hide(openItem, true); }
      else freeze(true);
      openItem = it;
      it.classList.add('is-open');
      setExpanded(it, true);
      warm();
      show(it, switching);
      hdr.classList.add('is-mega-open');
    };
    const close = (focusBack) => {
      clearTimeout(openT); clearTimeout(closeT);
      if (!openItem) return;
      const it = openItem;
      openItem = null;
      it.classList.remove('is-open');
      setExpanded(it, false);
      hide(it, false);
      hdr.classList.remove('is-mega-open', 'is-switching');
      freeze(false);
      if (focusBack) { const t = it.querySelector('a[data-ev-top], [data-ev-toggle]'); if (t) t.focus(); }
    };
    items.forEach((it) => {
      it.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !mqDesk.matches) return;
        clearTimeout(closeT); clearTimeout(openT);
        openT = setTimeout(() => open(it), openItem ? SWITCH_DELAY : OPEN_DELAY);
      });
      it.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        clearTimeout(openT);
        closeT = setTimeout(() => { if (openItem === it) close(false); }, CLOSE_DELAY);
      });
      it.querySelectorAll('[data-ev-toggle]').forEach((b) => b.addEventListener('click', (e) => {
        e.preventDefault();
        if (openItem === it) close(false); else open(it);
      }));
      /* a touch screen at computer width: the first tap on a label opens its panel, the second follows the link */
      const top = it.querySelector('a[data-ev-top]');
      if (top) {
        let lastPointer = 'mouse';
        top.addEventListener('pointerdown', (e) => { lastPointer = e.pointerType; });
        top.addEventListener('click', (e) => {
          if (lastPointer === 'mouse' || !mqDesk.matches) return;
          if (openItem !== it) { e.preventDefault(); open(it); }
        });
      }
      /* keyboard: arrow down on a label opens its panel and moves into it */
      const label = it.querySelector('[data-ev-top]');
      if (label) label.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowDown') return;
        e.preventDefault();
        open(it);
        const p = panelOf(it);
        const first = p && p.querySelector('a[href], button');
        if (first) setTimeout(() => first.focus(), 40);
      });
      it.addEventListener('focusout', (e) => {
        if (openItem === it && e.relatedTarget && !it.contains(e.relatedTarget)) close(false);
      });
    });
    if (bars) bars.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse' && mqDesk.matches) warm(); }, { once: true });
    if (nav) nav.addEventListener('focusin', warm, { once: true });
    const onMove = (e) => {
      if (e.pointerType !== 'mouse' || !mqDesk.matches) return;
      D.removeEventListener('pointermove', onMove);
      setTimeout(warm, 700);
    };
    D.addEventListener('pointermove', onMove, { passive: true });
    if (scrim) scrim.addEventListener('click', () => close(false));
    D.addEventListener('click', (e) => { if (openItem && !openItem.contains(e.target)) close(false); });

    /* ---------- the navigation fits itself (computer width) ---------- */
    const lists = nav ? [...nav.querySelectorAll('.ev-nav')] : [];
    const fit = () => {
      if (!lists.length || !mqDesk.matches) return;
      const steps = ['is-nav-tight', 'is-nav-small'];
      hdr.classList.remove(...steps);
      const over = () => lists.some((l) => l.scrollWidth > l.clientWidth + 1);
      for (const s of steps) { if (!over()) break; hdr.classList.add(s); }
    };

    /* ---------- the search overlay ---------- */
    const sov = hdr.querySelector('[data-ev-search-panel]');
    const sovPanel = sov && sov.querySelector('[role="dialog"]');
    const sovInput = sov && sov.querySelector('input[type="search"]');
    const findBtns = [...hdr.querySelectorAll('[data-ev-search-open]')];
    let sOpen = false, sLast = null, sHideT = 0;
    const openSearch = () => {
      if (!sov || sOpen) return;
      close(false);
      closeDrawer(true);
      sOpen = true;
      freeze(true);
      clearTimeout(sHideT);
      sLast = D.activeElement;
      sov.hidden = false;
      findBtns.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      D.body.classList.add('ev-search-open');
      sov.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      requestAnimationFrame(() => requestAnimationFrame(() => sov.classList.add('is-open')));
      setTimeout(() => { if (sovInput) { sovInput.focus({ preventScroll: true }); try { const n = sovInput.value.length; sovInput.setSelectionRange(n, n); } catch (e) { /* type=search */ } } }, 60);
    };
    const closeSearch = (noFocus) => {
      if (!sov || !sOpen) return;
      sOpen = false;
      sov.classList.remove('is-open');
      findBtns.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      D.body.classList.remove('ev-search-open');
      const ps = sov.querySelector('predictive-search');
      if (ps && typeof ps.close === 'function') { try { ps.close(); } catch (e) { /* older Dawn */ } }
      sHideT = setTimeout(() => { if (!sOpen) sov.hidden = true; }, mqReduce.matches ? 0 : 380);
      freeze(false);
      if (!noFocus && sLast && sLast.focus) sLast.focus();
    };
    if (sov) {
      findBtns.forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); if (sOpen) closeSearch(); else openSearch(); }));
      sov.querySelectorAll('[data-ev-search-close]').forEach((b) => b.addEventListener('click', () => closeSearch()));
      sov.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          /* the first Escape clears a highlighted suggestion (Dawn), the next one closes the overlay */
          const ps = sov.querySelector('predictive-search');
          if (ps && ps.querySelector('[aria-selected="true"]')) return;
          e.preventDefault();
          closeSearch();
          return;
        }
        if (e.key !== 'Tab' || !sovPanel) return;
        const f = focusablesIn(sovPanel);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && D.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && D.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }

    /* ---------- the drawer ---------- */
    const drawer = hdr.querySelector('[data-ev-drawer]');
    const dPanel = drawer && drawer.querySelector('[data-ev-drawer-panel]');
    const views = drawer && drawer.querySelector('[data-ev-views]');
    const rootView = views && views.querySelector('[data-ev-view="root"]');
    let dOpen = false, lastFocus = null, hideT = 0, logoDone = false, current = rootView, opener = null;
    const fillLogo = () => {
      if (logoDone || !drawer) return;
      logoDone = true;
      const slot = drawer.querySelector('[data-ev-logo-slot]');
      const logo = hdr.querySelector('.ev-hdr__logo .ev-logo');
      if (slot && logo) {
        const c = logo.cloneNode(true);
        c.classList.remove('ev-logo--animate');
        c.setAttribute('aria-hidden', 'true');
        c.removeAttribute('role');
        c.removeAttribute('aria-label');
        c.querySelectorAll('.ev-logo__band, .ev-logo__shine').forEach((m) => m.remove());
        slot.appendChild(c);
      }
    };
    /* copy a computer panel into the drawer; text columns become accordions */
    let uid = 0;
    const buildView = (key) => {
      let v = views.querySelector('[data-ev-view="' + key + '"]');
      if (v) return v;
      const row = rootView.querySelector('[data-ev-go="' + key + '"]');
      const src = hdr.querySelector('.ev-hdr__nav [data-ev-panel="' + key + '"]');
      if (!src) return null;
      v = D.createElement('div');
      v.className = 'ev-drawer__view';
      v.setAttribute('data-ev-view', key);
      const sub = D.createElement('div');
      sub.className = 'ev-drawer__sub';
      const back = D.createElement('button');
      back.type = 'button';
      back.className = 'ev-drawer__back';
      back.innerHTML = ICON_CHEV_LEFT + '<span>' + (drawer.dataset.backLabel || 'Menu') + '</span>';
      back.addEventListener('click', () => go(null));
      const title = D.createElement('p');
      title.className = 'ev-drawer__title';
      title.textContent = row ? row.querySelector('.ev-drawer__rowtext').textContent.trim() : '';
      sub.append(back, title);
      v.appendChild(sub);
      const copy = src.cloneNode(true);
      copy.hidden = false;
      copy.classList.remove('is-open');
      copy.removeAttribute('id');
      copy.querySelectorAll('[id]').forEach((el) => {
        const nid = el.id + '-d';
        copy.querySelectorAll('[aria-controls="' + el.id + '"]').forEach((a) => a.setAttribute('aria-controls', nid));
        copy.querySelectorAll('[aria-labelledby="' + el.id + '"]').forEach((a) => a.setAttribute('aria-labelledby', nid));
        el.id = nid;
      });
      copy.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
      copy.querySelectorAll('[data-ev-mcol]').forEach((col) => {
        if (col.getAttribute('data-ev-mcol') === 'open') return;
        const head = col.querySelector(':scope > .ev-mcol__title');
        if (!head) return;
        const bodyId = 'EvAcc-' + (++uid) + '-' + key;
        const body = D.createElement('div');
        body.className = 'ev-mcol__body';
        body.id = bodyId;
        body.hidden = true;
        [...col.children].forEach((ch) => { if (ch !== head) body.appendChild(ch); });
        const t = D.createElement('button');
        t.type = 'button';
        t.className = 'ev-mcol__toggle';
        t.setAttribute('aria-expanded', 'false');
        t.setAttribute('aria-controls', bodyId);
        t.innerHTML = '<span>' + head.textContent.trim() + '</span>' + ICON_PLUS;
        t.addEventListener('click', () => {
          const on = t.getAttribute('aria-expanded') !== 'true';
          t.setAttribute('aria-expanded', on ? 'true' : 'false');
          body.hidden = !on;
        });
        col.setAttribute('data-ev-acc', '');
        col.append(t, body);
      });
      v.appendChild(copy);
      views.appendChild(v);
      if (W.BO && W.BO.init) W.BO.init(v);
      return v;
    };
    const go = (key) => {
      if (!views) return;
      const next = key ? buildView(key) : rootView;
      if (!next || next === current) return;
      const prev = current;
      if (key) {
        opener = rootView.querySelector('[data-ev-go="' + key + '"]');
        rootView.classList.add('is-behind');
        rootView.classList.remove('is-current');
        void next.offsetWidth;
        next.classList.add('is-current');
        next.scrollTop = 0;
        if (opener) opener.setAttribute('aria-expanded', 'true');
        setTimeout(() => { const b = next.querySelector('.ev-drawer__back'); if (b) b.focus(); }, 60);
      } else {
        prev.classList.remove('is-current');
        rootView.classList.remove('is-behind');
        rootView.classList.add('is-current');
        if (opener) { opener.setAttribute('aria-expanded', 'false'); setTimeout(() => opener.focus(), 60); }
      }
      current = next;
    };
    const openDrawer = () => {
      if (!drawer || dOpen) return;
      close(false);
      closeSearch(true);
      fillLogo();
      dOpen = true;
      freeze(true);
      clearTimeout(hideT);
      lastFocus = D.activeElement;
      drawer.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => drawer.classList.add('is-open')));
      hdr.querySelectorAll('[data-ev-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      D.body.classList.add('overflow-hidden', 'ev-drawer-open');
      setTimeout(() => { const c = drawer.querySelector('.ev-drawer__close'); (c || dPanel).focus(); }, 80);
    };
    function closeDrawer(noFocus) {
      if (!drawer || !dOpen) return;
      dOpen = false;
      drawer.classList.remove('is-open');
      hdr.querySelectorAll('[data-ev-drawer-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      D.body.classList.remove('overflow-hidden', 'ev-drawer-open');
      freeze(false);
      hideT = setTimeout(() => {
        if (dOpen) return;
        drawer.hidden = true;
        if (current !== rootView) { current.classList.remove('is-current'); rootView.classList.remove('is-behind'); rootView.classList.add('is-current'); current = rootView; }
      }, mqReduce.matches ? 0 : 520);
      if (!noFocus && lastFocus && lastFocus.focus) lastFocus.focus();
    }
    if (drawer) {
      hdr.querySelectorAll('[data-ev-drawer-open]').forEach((b) => b.addEventListener('click', openDrawer));
      drawer.querySelectorAll('[data-ev-drawer-close]').forEach((b) => b.addEventListener('click', () => closeDrawer()));
      drawer.querySelectorAll('[data-ev-go]').forEach((b) => b.addEventListener('click', () => go(b.getAttribute('data-ev-go'))));
      drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
        if (e.key !== 'Tab') return;
        const f = focusablesIn(dPanel);
        if (!f.length) return;
        const first = f[0], last = f[f.length - 1];
        if (e.shiftKey && (D.activeElement === first || D.activeElement === dPanel)) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && D.activeElement === last) { e.preventDefault(); first.focus(); }
      });
      /* a "Chat with us" link closes the drawer first, so the chat window is not hidden behind it */
      drawer.addEventListener('click', (e) => { if (e.target.closest('[data-ev-chat]')) closeDrawer(true); }, true);
    }
    /* the Help panel's chat button: close the panel first */
    hdr.addEventListener('click', (e) => { if (openItem && e.target.closest && e.target.closest('.ev-hdr__nav [data-ev-chat]')) close(false); });

    D.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openItem) close(true); });

    const onLayout = () => {
      if (mqDesk.matches) closeDrawer(true); else close(false);
      writeH();
      fit();
    };
    mqDesk.addEventListener('change', onLayout);
    let rT = 0, lastW = 0;
    W.addEventListener('resize', () => {
      clearTimeout(rT);
      rT = setTimeout(() => { const w = W.innerWidth; if (w !== lastW) { lastW = w; fit(); writeH(); } }, 150);
    }, { passive: true });
    if (D.fonts && D.fonts.ready) D.fonts.ready.then(fit);
    W.addEventListener('load', fit, { once: true });
    fit();
    writeH();

    /* ---------- something went into the cart: the bag hops, the count pops, the logo glints ---------- */
    let lastBump = 0, lastCount = -1;
    const bump = () => {
      const now = Date.now();
      if (mqReduce.matches || now - lastBump < 1200) return;
      lastBump = now;
      const link = hdr.querySelector('#cart-icon-bubble');
      if (link) {
        link.classList.remove('is-bump');
        void link.offsetWidth;
        link.classList.add('is-bump');
        clearTimeout(link.evBumpT);
        link.evBumpT = setTimeout(() => link.classList.remove('is-bump'), 1600);
      }
      glint();
    };
    D.addEventListener('ev:cart:added', bump);
    D.addEventListener('ev:cart:updated', (e) => {
      const c = e.detail && e.detail.cart;
      if (!c || typeof c.item_count !== 'number') return;
      if (lastCount >= 0 && c.item_count > lastCount) bump();
      lastCount = c.item_count;
    });

    hdr.evHeader = { open, close, openSearch, closeSearch, openDrawer, closeDrawer, go, bump, setCompact, setTucked };
  };

  const init = (scope) => (scope || D).querySelectorAll('[data-ev-header]').forEach(bind);
  window.EVHeader = { init };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D)); else init(D);
  D.addEventListener('shopify:section:load', (e) => init(e.target));
})();
